<template>
  <div>

    <div class="users-heading d-flex justify-content-between align-items-center">
      <div>
        <h1 class="mb-1">Users</h1>
        <p class="text-muted mb-0">Manage team members, access roles, and lead limits.</p>
      </div>
      <router-link class="btn btn-primary" :to="{ name: 'UserNew' }"><span class="mr-1 fas fa-plus"></span> New user</router-link>
    </div>

    <UserManagementTabs />
    <div class="card">
      <div class="card-body">
        <div class="users-toolbar">
          <div class="d-flex align-items-center flex-wrap">
            <div class="search-wrapper mr-3">
              <SearchInput v-model="search" @input="startSearch" placeholder="Search users..."/>
            </div>
            <div class="role-filter-wrapper mr-3">
              <multiselect
                :value="selectedRole"
                :options="roleOptions"
                label="label"
                track-by="value"
                placeholder="All roles"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                :show-labels="false"
                @input="filterByRole"
              >
                <span slot="noResult">No roles found</span>
              </multiselect>
            </div>
            <b-form-checkbox class="mb-0" v-if="search" v-model="searchArchived" @input="startSearch" :unchecked-value="0" :value="1">Include disabled</b-form-checkbox>
          </div>
          <div class="pt-3">
            <PageLinks :current-page="currentPage" :has-next-page="hasNextPage" :on-page-change="updatePageUrlParam" :is-disabled="loading" />
          </div>
        </div>

        <div class="position-relative">
          <div v-if="loading" class="loading-overlay">
            <LoadingIndicator centered />
          </div>

          <div class="table-responsive users-table-wrap">
          <table class="table users-table mb-0">
            <thead>
              <tr>
                <TableSortableHeader label="User" field="name" />
                <TableSortableHeader label="Contact" field="email" />
                <th scope="col" class="text-center">Lead limits</th>
                <th scope="col">Roles</th>
                <th scope="col">Last Login</th>
                <TableSortableHeader label="Created" field="createdAt" />
              </tr>
            </thead>
            <tbody :class="{ 'table-loading': loading }">
              <tr v-for="user in users" :key="user.id"  :class="rowClassName(user)">
                <td>
                  <div class="user-cell">
                  <div v-if="isAdmin" class="user-avatar">
                    <AvatarForm
                      :avatarForm="{ photo: user.photo, username: user.username }"
                      :userId="user.id"
                      :onFinishSubmitting="onAvatarUpdated"
                    />
                  </div>
                  <div v-else class="user-avatar">
                    <Avatar :size="38" :photo="user.photo" />
                  </div>
                  <div class="user-identity">
                    <b-link class="user-name" :to="{ name: 'UserDetails', params: { id: user.id } }">{{ user.name || user.username }}</b-link>
                    <span class="user-meta">#{{ user.id }} · {{ shortenNexusAddress(user.username) }}</span>
                    <CountryFlag v-if="user.country" :country="user.country" size="small"/>
                  </div>
                  </div>
                </td>
                <td>
                  <a v-if="user.email" class="contact-link" :href="`mailto:${user.email}`">{{ shortenNexusAddress(user.email) }}</a>
                  <span v-else class="text-muted">—</span>
                  <a v-if="user.phone" class="contact-link contact-phone" :href="`tel:${phoneHref(user.phone)}`">{{ user.phone | formatPhone }}</a>
                  <small v-else class="d-block text-muted">No phone</small>
                </td>
                <td class="text-center"><div class="limit-values"><span><small>Daily</small>{{ user.dailyLimit || 0 }}</span><span><small>Fresh</small>{{ user.freshLimit || 0 }}</span></div></td>
                <td><span v-for="role in user.roles || []" :key="role.id || role.name" class="role-pill">{{ role.name }}</span><span v-if="!user.roles || !user.roles.length" class="text-muted">—</span></td>
                <td><div v-if="user.lastLogin">{{ user.lastLogin.createdAt | formatDate }}<small class="d-block text-muted">{{ user.lastLogin.ip }}</small></div><span v-else class="text-muted">Never</span></td>
                <td>{{ user.createdAt | formatDate }}</td>
              </tr>
              <tr v-if="!loading && users.length === 0"><td colspan="6" class="empty-users text-center text-muted">No users found.</td></tr>
            </tbody>
          </table>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import pageLinksMixin from '@/mixins/pageLinks';
import { debounce } from 'debounce';
import permissionsMixin from '/src/mixins/permissions';
import LoadingIndicator from '@/components/shared/LoadingIndicator.vue';
import TableSortableHeader from '@/components/shared/TableSortableHeader';
import CountryFlag from 'vue-country-flag';
import SearchInput from '@/components/shared/SearchInput.vue'
import AvatarForm from '@/components/shared/form/AvatarForm';
import Avatar from '@/components/shared/avatar/Avatar';
import UserManagementTabs from '@/components/pages/users/UserManagementTabs.vue';

const updateSearchUrlParamDebounced = debounce(function (router, search) {
  router.push({ query: search });
}, 800);

export default {
  components: {
    LoadingIndicator,
    TableSortableHeader,
    CountryFlag,
    Avatar,
    SearchInput,
    AvatarForm,
    UserManagementTabs
  },
  mixins: [pageLinksMixin, permissionsMixin],
  data() {
    return {
      users: [],
      loading: false,
      search: '',
      searchArchived: false,
      roleId: '',
      roles: [],
      fetchRequestId: 0,
    };
  },
  computed: {
    hasNextPage() {
      return this.users.length === this.perPage;
    },
    sortOrder() {
      const { sort } = this.$route.query;
      return sort;
    },
    isAdmin() {
      return this.$store.getters['auth/tokenCan']('admin')
    },
    roleOptions() {
      return [{ value: '', label: 'All roles' }, ...this.roles.map(role => ({ value: String(role.id), label: role.name }))];
    },
    selectedRole() {
      return this.roleOptions.find(role => role.value === String(this.roleId)) || this.roleOptions[0];
    },
  },
  created() {
    this.fetchRoles();
  },
  methods: {
    onAvatarUpdated() {
      this.fetch()
    },
    async fetch() {
      const requestId = ++this.fetchRequestId;
      this.loading = true;

      let opts = {
        page: this.currentPage,
        per_page: this.perPage,
        search: this.search,
        searchArchived: this.searchArchived ? 1 : 0,
        roleId: this.roleId || undefined,
        sort: this.sortOrder,
      };

      if (this.$route.query.archived) opts.isArchived = 1;
      
      const response = await ApiClient.users.get({params: opts});
      if (requestId !== this.fetchRequestId) return;
      this.loading = false;
      if (response.status == 200) {
        this.users = response.data.data;
        this.searchStats = response.data.search;
      }
    },
    startSearch() {
      updateSearchUrlParamDebounced(this.$router, {
        ...this.$route.query,
        page: 1,
        search: this.search || undefined,
        searchArchived: this.searchArchived ? 1 : undefined,
        roleId: this.roleId || undefined,
      });
    },
    filterByRole(role) {
      this.roleId = role ? role.value : '';
      this.$router.push({
        query: {
          ...this.$route.query,
          page: 1,
          roleId: this.roleId || undefined,
        },
      });
    },
    async fetchRoles() {
      const response = await ApiClient.roles.get({ params: { per_page: 250 } });
      if (response.status === 200) this.roles = response.data.data;
    },

    rowClassName(user) {
      return user.deletedAt ? 'text-muted' : '';
    },
    shortenNexusAddress(value) {
      if (!value) return '—';
      return value.replace('@nexusautotransport.com', '@nexus…');
    },
    phoneHref(value) {
      return String(value || '').replace(/[^+\d]/g, '');
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler: function () {
        const { search, searchArchived, roleId } = this.$route.query;
        this.search = search || '';
        this.searchArchived = searchArchived ? 1 : 0;
        this.roleId = roleId || '';
        this.fetch();
      },
    },
  },

};
</script>

<style lang="scss" scoped>
.search-wrapper {
  width: 320px;
  max-width: 100%;
}
.role-filter-wrapper {
  width: 220px;
  max-width: 100%;
}
.select-wrapper {
  max-width: 300px;
}

.table-loading {
  opacity: 0.5;
}
.loading-overlay {
  position: absolute;
  left: 50%;
  top: 50%;
}
/*
tbody td {
  padding: 5px;
}
*/
td {
  vertical-align: middle;
}

.text-muted a {
  color: inherit;
}

.users-heading { margin-bottom: 1.25rem; }
.users-toolbar { padding-bottom: 1rem; }
.users-table-wrap { border: 0; border-radius: 0; }
.users-table thead th { padding: 0.8rem 0.9rem; border-top: 0; border-bottom: 1px solid #dfe3e8; background: #f6f7f9; color: #5f6977; font-size: 0.75rem; font-weight: 700; letter-spacing: 0.035em; text-transform: uppercase; white-space: nowrap; }
.users-table tbody td { padding: 0.75rem 0.9rem; border-top-color: #e7eaee; color: #343a40; }
.users-table tbody tr:hover { background: #f7f9fb; }
.user-cell { display: flex; align-items: center; min-width: 210px; }
.user-avatar { flex: 0 0 auto; margin-right: 0.75rem; }
.user-identity { min-width: 0; }
.user-name { display: inline-block; font-weight: 700; }
.user-meta { display: block; max-width: 230px; overflow: hidden; color: #6c7580; font-size: 0.78rem; text-overflow: ellipsis; white-space: nowrap; }
.contact-link { display: block; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.contact-phone { margin-top: 0.18rem; color: #6c7580; font-size: 0.78rem; }
.contact-phone:hover { color: #1676df; }
.limit-values { display: inline-flex; gap: 0.45rem; }
.limit-values span { display: flex; min-width: 48px; padding: 0.3rem 0.45rem; flex-direction: column; border-radius: 0.3rem; background: #f0f2f5; font-weight: 700; }
.limit-values small { color: #7a8491; font-size: 0.65rem; font-weight: 500; text-transform: uppercase; }
.role-pill { display: inline-block; margin: 0.1rem 0.25rem 0.1rem 0; padding: 0.22rem 0.5rem; border-radius: 999px; color: #3c628a; background: #e7f1fb; font-size: 0.75rem; font-weight: 600; }
.empty-users { padding: 2.5rem !important; }
.dark-style {
  .users-table-wrap { border: 0; }
  .users-table thead th { border-bottom-color: rgba(255, 255, 255, 0.12); background: transparent; color: #aeb4bd; }
  .users-table tbody td { border-top-color: rgba(255, 255, 255, 0.06); color: inherit; }
  .users-table tbody tr:hover { background: rgba(255, 255, 255, 0.035); }
  .user-meta { color: #aeb4bd; }
  .limit-values span { background: rgba(255, 255, 255, 0.06); }
  .limit-values small { color: #aeb4bd; }
  .role-pill { color: #8fc7ff; background: rgba(44, 123, 204, 0.18); }
}

@media (max-width: 767px) {
  .users-heading { align-items: flex-start !important; gap: 1rem; }
  .search-wrapper { width: 100%; margin-right: 0 !important; }
  .role-filter-wrapper { width: 100%; margin-top: 0.5rem; margin-right: 0 !important; }
}

</style>
